import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { initGoogleAPI, isSignedIn, signIn, signOut, getCalendarList } from '../../../utils/googleCalendarService';

const GoogleCalendarSettings = ({ preferences, onChange }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState('primary');
  const [error, setError] = useState(null);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      await initGoogleAPI();
      const signedIn = isSignedIn();
      setIsConnected(signedIn);
      
      if (signedIn) {
        await loadCalendars();
      }
    } catch (err) {
      console.error('Error checking Google Calendar connection:', err);
    }
  };

  const loadCalendars = async () => {
    try {
      const calendarList = await getCalendarList();
      setCalendars(calendarList);
    } catch (err) {
      console.error('Error loading calendars:', err);
    }
  };

  const handleConnect = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await signIn();
      setIsConnected(true);
      await loadCalendars();
      
      // Update preferences
      onChange({
        ...preferences,
        googleCalendarConnected: true,
        googleCalendarId: 'primary',
      });
    } catch (err) {
      console.error('Error connecting to Google Calendar:', err);
      setError('Failed to connect to Google Calendar. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await signOut();
      setIsConnected(false);
      setCalendars([]);
      
      // Update preferences
      onChange({
        ...preferences,
        googleCalendarConnected: false,
        googleCalendarId: null,
      });
    } catch (err) {
      console.error('Error disconnecting from Google Calendar:', err);
      setError('Failed to disconnect. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCalendarChange = (calendarId) => {
    setSelectedCalendar(calendarId);
    onChange({
      ...preferences,
      googleCalendarId: calendarId,
    });
  };

  const handleToggleSync = (key) => {
    onChange({
      ...preferences,
      [key]: !preferences?.[key],
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <Icon name="calendar" size={24} className="text-primary" />
        <h2 className="text-h3 font-heading text-foreground">
          Google Calendar Integration
        </h2>
      </div>
      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2">
          <Icon name="alert-circle" size={16} className="text-destructive mt-0.5" />
          <span className="text-sm text-destructive">{error}</span>
        </div>
      )}
      <div className="space-y-6">
        {/* Connection Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <Icon 
              name={isConnected ? "check-circle" : "circle"} 
              size={20} 
              className={`mt-1 ${isConnected ? 'text-success' : 'text-muted-foreground'}`} 
            />
            <div>
              <h3 className="font-medium text-foreground mb-1">
                Connection Status
              </h3>
              <p className="text-sm text-muted-foreground">
                {isConnected ? 'Connected to Google Calendar' : 'Not connected'}
              </p>
            </div>
          </div>
          
          {isConnected ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDisconnect}
              disabled={isLoading}
              loading={isLoading}
            >
              Disconnect
            </Button>
          ) : (
            <Button
              variant="default"
              size="sm"
              iconName="link"
              onClick={handleConnect}
              disabled={isLoading}
              loading={isLoading}
            >
              Connect Google Calendar
            </Button>
          )}
        </div>

        {/* Calendar Selection */}
        {isConnected && calendars?.length > 0 && (
          <div className="flex items-start gap-3">
            <Icon name="list" size={20} className="text-muted-foreground mt-3" />
            <div className="flex-1">
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Calendar
              </label>
              <p className="text-sm text-muted-foreground mb-3">
                Choose which Google Calendar to sync with
              </p>
              <select
                value={selectedCalendar}
                onChange={(e) => handleCalendarChange(e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {calendars?.map((calendar) => (
                  <option key={calendar?.id} value={calendar?.id}>
                    {calendar?.summary}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Auto Import Toggle */}
        {isConnected && (
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <Icon name="download" size={20} className="text-muted-foreground mt-1" />
              <div>
                <h3 className="font-medium text-foreground mb-1">
                  Auto Import Events
                </h3>
                <p className="text-sm text-muted-foreground">
                  Automatically import events from Google Calendar
                </p>
              </div>
            </div>
            <button
              onClick={() => handleToggleSync('googleCalendarAutoImport')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                preferences?.googleCalendarAutoImport ? 'bg-primary' : 'bg-muted'
              }`}
              aria-label="Toggle auto import"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preferences?.googleCalendarAutoImport ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        )}

        {/* Two-way Sync Toggle */}
        {isConnected && (
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <Icon name="refresh-cw" size={20} className="text-muted-foreground mt-1" />
              <div>
                <h3 className="font-medium text-foreground mb-1">
                  Two-way Sync
                </h3>
                <p className="text-sm text-muted-foreground">
                  Sync changes both ways between app and Google Calendar
                </p>
              </div>
            </div>
            <button
              onClick={() => handleToggleSync('googleCalendarTwoWaySync')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                preferences?.googleCalendarTwoWaySync ? 'bg-primary' : 'bg-muted'
              }`}
              aria-label="Toggle two-way sync"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preferences?.googleCalendarTwoWaySync ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        )}

        {/* Info Box */}
        {!isConnected && (
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="info" size={16} className="text-muted-foreground mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="mb-2">
                  Connect your Google Calendar to:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Import events automatically</li>
                  <li>Sync family activities to Google Calendar</li>
                  <li>Keep everything in sync across devices</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleCalendarSettings;
